import { useAuthStore } from './store';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { accessToken } = useAuthStore.getState();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });
  
  const data: ApiResponse<T> = await response.json();
  
  if (!data.success) {
    throw new Error(data.error?.message || 'API request failed');
  }
  
  return data.data as T;
}

// Auth
export interface LoginResponse {
  tenant: { tenantId: string; email: string; createdAt: string };
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return fetchApi<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function signup(email: string, password: string): Promise<LoginResponse> {
  return fetchApi<LoginResponse>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

// Projects
export interface Project {
  projectId: string;
  tenantId: string;
  name: string;
  groupName: string | null;
  defaultBoardProvider: string | null;
  boardMetadata: string | null;
  createdAt: string;
}

export async function getProjects(): Promise<Project[]> {
  return fetchApi<Project[]>('/api/projects');
}

export async function getProject(projectId: string): Promise<Project> {
  return fetchApi<Project>(`/api/projects/${projectId}`);
}

export async function createProject(name: string, groupName?: string): Promise<Project> {
  return fetchApi<Project>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name, groupName }),
  });
}

export async function deleteProject(projectId: string): Promise<void> {
  return fetchApi<void>(`/api/projects/${projectId}`, {
    method: 'DELETE',
  });
}

// Project Tree
export interface MemoryFile {
  filename: string;
  type?: string;
  latestVersion: number;
}

export interface AspectTreeNode {
  aspectId: string;
  name: string;
  description: string | null;
  path: string;
  ordering: number;
  boardProvider: string | null;
  memoryFiles: MemoryFile[];
  children: AspectTreeNode[];
}

export interface ProjectTree {
  project: Project;
  aspects: AspectTreeNode[];
}

export async function getProjectTree(projectId: string): Promise<ProjectTree> {
  return fetchApi<ProjectTree>(`/api/projects/${projectId}/tree`);
}

// Aspects
export interface Aspect {
  aspectId: string;
  tenantId: string;
  projectId: string;
  parentAspectId: string | null;
  name: string;
  description: string | null;
  ordering: number;
  boardProvider: string | null;
  boardMetadata: string | null;
  isDeleted: number;
  createdAt: string;
}

export async function createAspect(
  projectId: string,
  name: string,
  description?: string,
  parentAspectId?: string
): Promise<Aspect> {
  return fetchApi<Aspect>(`/api/projects/${projectId}/aspects`, {
    method: 'POST',
    body: JSON.stringify({ name, description, parentAspectId }),
  });
}

export async function deleteAspect(aspectId: string, soft = true): Promise<void> {
  return fetchApi<void>(`/api/aspects/${aspectId}?soft=${soft}`, {
    method: 'DELETE',
  });
}

// Memory
export interface MemoryContent {
  filename: string;
  content: string;
  version: number;
  type?: string;
}

export async function getMemoryFiles(aspectId: string): Promise<MemoryFile[]> {
  return fetchApi<MemoryFile[]>(`/api/aspects/${aspectId}/memory`);
}

export async function getMemoryContent(aspectId: string, filename: string): Promise<MemoryContent> {
  return fetchApi<MemoryContent>(`/api/aspects/${aspectId}/memory/${filename}`);
}

export async function updateMemoryContent(
  aspectId: string,
  filename: string,
  content: string,
  type?: string
): Promise<MemoryContent> {
  return fetchApi<MemoryContent>(`/api/aspects/${aspectId}/memory/${filename}`, {
    method: 'PUT',
    body: JSON.stringify({ content, type }),
  });
}

// Brain Dump
export interface BrainDumpResult {
  updatedDocuments: Array<{
    filename: string;
    content: string;
    changesSummary: string;
  }>;
  proposedTickets: Array<{
    title: string;
    description: string;
    priority: string;
    labels?: string[];
  }>;
  summary: string;
}

export async function processBrainDump(aspectId: string, text: string): Promise<BrainDumpResult> {
  return fetchApi<BrainDumpResult>(`/api/aspects/${aspectId}/process-dump`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// Summary
export interface SummaryResult {
  aspectId: string;
  summary: string;
  includingChildren: boolean;
}

export async function getAspectSummary(aspectId: string, includeChildren = false): Promise<SummaryResult> {
  return fetchApi<SummaryResult>(`/api/aspects/${aspectId}/summary`, {
    method: 'POST',
    body: JSON.stringify({ includeChildren }),
  });
}

// Integration Status
export interface IntegrationStatus {
  connected: boolean;
}

export async function getJiraStatus(): Promise<IntegrationStatus> {
  return fetchApi<IntegrationStatus>('/integrations/jira/status');
}

export async function getLinearStatus(): Promise<IntegrationStatus> {
  return fetchApi<IntegrationStatus>('/integrations/linear/status');
}

